<template>
  <div v-if="inline || visible" class="app-modal-overlay"
    :class="{
      'app-modal-overlay--bottom-sheet': bottomSheet && !nested,
      'app-modal-overlay--inline': inline,
      'app-modal-overlay--nested': nested
    }"
    :style="overlayStyle"
    @click="onOverlayClick"
    @mousedown="stopBubble"
    @touchstart="stopBubble">
    <div class="app-modal"
      :class="[modalClass, {
        'app-modal--bottom-sheet': bottomSheet && !nested,
        'app-modal--inline': inline,
        'app-modal--nested': nested
      }]"
      :style="modalStyle"
      @click.stop>
      <div class="app-modal-header" v-if="$slots.header || title || showCloseButton">
        <slot name="header">
          <h3 class="app-modal-title">{{ title }}</h3>
        </slot>
        <button v-if="showCloseButton" type="button" class="app-modal-close" @click.stop="close">
          <span>&times;</span>
        </button>
      </div>
      <div class="app-modal-body" :style="(maxHeight || nested) ? { overflowY: 'auto', flex: 1, minHeight: 0 } : {}">
        <slot></slot>
      </div>
      <div class="app-modal-footer" v-if="$slots.footer">
        <slot name="footer"></slot>
      </div>
    </div>
  </div>
</template>

<script>
const openModals = [];
let popstateSuppressed = false;
let popstateSuppressTimer = null;

function registerModal(vm) {
  if (openModals.indexOf(vm) === -1) {
    openModals.push(vm);
  }
  vm.stackIndex = openModals.indexOf(vm);
}

function unregisterModal(vm) {
  const index = openModals.indexOf(vm);
  if (index !== -1) {
    openModals.splice(index, 1);
  }
  const ignoreUntil = Date.now() + 350;
  openModals.forEach((modal, i) => {
    modal._ignoreOverlayUntil = ignoreUntil;
    modal.stackIndex = i;
  });
}

function getTopModal() {
  return openModals[openModals.length - 1] || null;
}

function syncBodyOverflow() {
  if (typeof document === "undefined") return;
  document.body.style.overflow = openModals.length ? "hidden" : "";
}

function suppressNextPopstate() {
  popstateSuppressed = true;
  if (popstateSuppressTimer) {
    clearTimeout(popstateSuppressTimer);
  }
  popstateSuppressTimer = setTimeout(() => {
    popstateSuppressed = false;
    popstateSuppressTimer = null;
  }, 100);
}

export default {
  name: "AppModal",
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ""
    },
    width: {
      type: String,
      default: "75%"
    },
    showCloseButton: {
      type: Boolean,
      default: true
    },
    closeOnOverlay: {
      type: Boolean,
      default: true
    },
    maxHeight: {
      type: String,
      default: ""
    },
    bottomSheet: {
      type: Boolean,
      default: false
    },
    inline: {
      type: Boolean,
      default: false
    },
    nested: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      stackIndex: 0
    };
  },
  created() {
    this._modalHistoryState = null;
    this._popstateHandler = null;
    this._keydownHandler = null;
    this._ignoreOverlayUntil = 0;
  },
  methods: {
    close() {
      this.$emit("update:visible", false);
      this.$emit("close");
    },
    stopBubble(e) {
      if (e) e.stopPropagation();
    },
    onOverlayClick(e) {
      if (e) {
        e.stopPropagation();
        if (e.target !== e.currentTarget) return;
      }
      this.handleOverlayClick(e);
    },
    handleOverlayClick() {
      if (getTopModal() !== this) return;
      if (this._ignoreOverlayUntil && Date.now() < this._ignoreOverlayUntil) return;
      if (this.closeOnOverlay) {
        this.close();
      }
    },
    popOwnHistory() {
      if (this._modalHistoryState && history.state && history.state.appModal && history.state.id === this._modalHistoryState.id) {
        suppressNextPopstate();
        history.back();
      }
      this._modalHistoryState = null;
    }
  },
  computed: {
    overlayStyle() {
      if (this.inline) return {};
      return {
        zIndex: 1100 + (this.stackIndex || 0) * 100
      };
    },
    modalStyle() {
      const style = {
        width: this.width,
        maxWidth: this.width
      };
      const maxHeight = this.nested && (!this.maxHeight || this.maxHeight === "100%")
        ? "85vh"
        : this.maxHeight;
      if (maxHeight) {
        style.maxHeight = maxHeight;
        style.display = "flex";
        style.flexDirection = "column";
      }
      return style;
    },
    modalClass() {
      return [];
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (this.inline) return;
        if (val) {
          registerModal(this);
          if (!this._modalHistoryState) {
            this._modalHistoryState = { appModal: true, id: Date.now() + Math.random() };
            history.pushState(this._modalHistoryState, "");
          }
          syncBodyOverflow();
        } else {
          const wasOpen = openModals.indexOf(this) !== -1;
          unregisterModal(this);
          if (wasOpen) {
            syncBodyOverflow();
            this.popOwnHistory();
          }
        }
      }
    }
  },
  mounted() {
    this._popstateHandler = () => {
      if (popstateSuppressed) return;
      if (this.inline || !this.visible || !this._modalHistoryState) return;
      if (getTopModal() !== this) return;
      this._modalHistoryState = null;
      this.close();
    };
    this._keydownHandler = (e) => {
      if (e.key !== "Escape" && e.keyCode !== 27) return;
      if (this.inline || !this.visible) return;
      if (getTopModal() !== this) return;
      e.preventDefault();
      e.stopPropagation();
      this.close();
    };
    window.addEventListener("popstate", this._popstateHandler);
    window.addEventListener("keydown", this._keydownHandler);
  },
  beforeDestroy() {
    unregisterModal(this);
    syncBodyOverflow();
    if (this._popstateHandler) {
      window.removeEventListener("popstate", this._popstateHandler);
    }
    if (this._keydownHandler) {
      window.removeEventListener("keydown", this._keydownHandler);
    }
    if (this._modalHistoryState && history.state && history.state.appModal && history.state.id === this._modalHistoryState.id) {
      suppressNextPopstate();
      history.back();
    }
    this._modalHistoryState = null;
  }
};
</script>

<style scoped>
.app-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
}

.app-modal-overlay--nested {
  z-index: 1200;
}

.app-modal-overlay--inline {
  position: static;
  display: block;
  padding: 0;
  background: transparent;
  overflow: visible;
}

/* .app-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  position: relative;
} */
.app-modal {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, .15);
  position: relative;

  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.app-modal--inline {
  width: 100% !important;
  max-width: none !important;
  max-height: none;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

.app-modal--inline .app-modal-body {
  padding: 0;
}

.app-modal-body {
  padding: 20px;
}

.app-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e5e5e5;
  background: #f8f8f8;
  border-radius: 16px 16px 0 0;
}

.app-modal-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
}

.app-modal-close {
  background: none;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  color: #666;
  transition: color 0.2s;
  min-width: 44px;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  -webkit-tap-highlight-color: transparent;
}

.app-modal-close:hover {
  color: #000;
}

.app-modal-close:active {
  background: rgba(0, 0, 0, 0.05);
}

.app-modal-footer {
  padding: 16px 20px;
  border-top: 1px solid #e5e5e5;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

/* Mobil: kök modaller full-width; iç içe (nested) modaller ayrı katman olarak kalır */
@media (max-width: 768px) {
  .app-modal:not(.app-modal--nested) {
    width: 100% !important;
    max-width: 100% !important;
  }

  .app-modal-body {
    padding: 16px;
  }

  .app-modal-overlay--nested {
    padding: 16px;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.45);
  }

  .app-modal--nested {
    width: calc(100% - 32px) !important;
    max-width: 720px !important;
    height: auto;
    min-height: 0;
    max-height: 85vh !important;
    border-radius: 14px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }

  .app-modal--nested .app-modal-header {
    padding: 16px;
    border-radius: 14px 14px 0 0;
  }

  .app-modal--nested .app-modal-title {
    font-size: 16px;
    line-height: 1.4;
  }

  .app-modal--nested .app-modal-close {
    min-width: 48px;
    min-height: 48px;
  }

  .app-modal--nested .app-modal-body {
    padding: 16px;
    font-size: 16px;
    line-height: 1.4;
  }

  .app-modal--nested .app-modal-footer {
    padding: 16px;
  }
}

/* Bottom sheet: isteğe bağlı, sadece mobilde aktif */
@media (max-width: 768px) {
  .app-modal-overlay--bottom-sheet {
    padding: 0;
    align-items: flex-end;
  }

  .app-modal--bottom-sheet {
    max-height: 90vh !important;
    border-radius: 16px 16px 0 0;
    display: flex;
    flex-direction: column;
  }

  .app-modal--bottom-sheet .app-modal-body {
    overflow-y: auto;
    flex: 1;
    min-height: 0;
  }

  .app-modal-footer {
    padding: 16px;
  }
}
</style>
